/**
 * VaultStack Email Service
 * Sends transactional emails via Resend
 * 
 * NO AI - Just reliable email delivery
 */

const RESEND_API_KEY = import.meta.env.VITE_RESEND_API_KEY;
const FROM_EMAIL = 'VaultStack <noreply@vaultstack-sbp.netlify.app>';

/**
 * Send welcome email after signup
 */
export async function sendWelcomeEmail(to: string, businessName: string) {
  try {
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${RESEND_API_KEY}`
      },
      body: JSON.stringify({
        from: FROM_EMAIL,
        to,
        subject: 'Welcome to VaultStack Small Business Platform',
        html: `
          <h1>Welcome to VaultStack!</h1>
          <p>Hi there,</p>
          <p>Your account for <strong>${businessName}</strong> is now active.</p>
          <p>Get started by logging in at: <a href="https://vaultstack-sbp.netlify.app">vaultstack-sbp.netlify.app</a></p>
          <p>What you can do:</p>
          <ul>
            <li>Manage your business profile</li>
            <li>Create custom templates</li>
            <li>Track inventory and bookings</li>
            <li>Generate quotes and invoices</li>
          </ul>
          <p>Need help? Reply to this email anytime.</p>
          <p>Cheers,<br/>The VaultStack Team</p>
        `
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send email');
    }

    return { success: true, data };
  } catch (error) {
    console.error('Welcome email error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Send quote notification email
 */
export async function sendQuoteEmail(
  to: string, 
  quoteDetails: {
    quoteNumber: string;
    customerName: string;
    totalAmount: number;
    businessName: string;
  }
) {
  try {
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${RESEND_API_KEY}`
      },
      body: JSON.stringify({
        from: FROM_EMAIL,
        to,
        subject: `Quote #${quoteDetails.quoteNumber} from ${quoteDetails.businessName}`,
        html: `
          <h1>Your Quote is Ready</h1>
          <p>Hi ${quoteDetails.customerName},</p>
          <p>${quoteDetails.businessName} has prepared a quote for you.</p>
          <p><strong>Quote #${quoteDetails.quoteNumber}</strong><br/>
          <strong>Total: $${quoteDetails.totalAmount.toFixed(2)}</strong></p>
          <p>Log in to your VaultStack dashboard to review and approve.</p>
          <p>Thanks for your business!</p>
        `
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send email');
    }

    return { success: true, data };
  } catch (error) {
    console.error('Quote email error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Send invoice notification email
 */
export async function sendInvoiceEmail(
  to: string,
  invoiceDetails: {
    invoiceNumber: string;
    customerName: string;
    totalAmount: number;
    dueDate: string;
    businessName: string;
  }
) {
  try {
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${RESEND_API_KEY}`
      },
      body: JSON.stringify({
        from: FROM_EMAIL,
        to,
        subject: `Invoice #${invoiceDetails.invoiceNumber} from ${invoiceDetails.businessName}`,
        html: `
          <h1>Invoice Ready</h1>
          <p>Hi ${invoiceDetails.customerName},</p>
          <p>${invoiceDetails.businessName} has sent you an invoice.</p>
          <p><strong>Invoice #${invoiceDetails.invoiceNumber}</strong><br/>
          <strong>Amount: $${invoiceDetails.totalAmount.toFixed(2)}</strong><br/>
          <strong>Due: ${invoiceDetails.dueDate}</strong></p>
          <p>Log in to your VaultStack dashboard to view and pay.</p>
          <p>Thank you!</p>
        `
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send email');
    }

    return { success: true, data };
  } catch (error) {
    console.error('Invoice email error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Send password reset email (Supabase handles this automatically)
 * This is just a wrapper if you want custom templates
 */
export async function sendPasswordResetEmail(to: string, resetUrl: string) {
  try {
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${RESEND_API_KEY}`
      },
      body: JSON.stringify({
        from: FROM_EMAIL,
        to,
        subject: 'Reset Your VaultStack Password',
        html: `
          <h1>Password Reset</h1>
          <p>Click the link below to reset your password:</p>
          <p><a href="${resetUrl}">Reset Password</a></p>
          <p>If you didn't request this, ignore this email.</p>
        `
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send email');
    }

    return { success: true, data };
  } catch (error) {
    console.error('Password reset email error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
